import { logger } from '@automata/utils';
import { createSelector } from '@reduxjs/toolkit';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import intersectionWith from 'lodash/intersectionWith';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import uniqWith from 'lodash/uniqWith';

import { selectFinalTasksByLabwareId } from './selectFinalTasksByLabwareId';
import type { InstrumentForLocation, ParsedSlot } from '../../types';
import { selectConnectedLabwareProperties } from '../selectors';
import * as LabwareErrors from '../shared/labwareErrors';
import {
  parseSlotString,
  parseSlotsToIndividualNumbers,
} from '../shared/utils';
import {
  selectActionTaskPropertiesStrict,
  selectInstrumentDataById,
} from '../workflow/selectors';
import type { LabwareSerializationError } from '../workflowJsonSelectors';

export const isDisjoint = <T>(a: T[], b: T[]): boolean => {
  return intersectionWith(a, b, isEqual).length === 0;
};

/** Object representing the different types of slot conflicts between labware and instruments,
 * within individual labware, and between different labware.
 *
 * labwareWithOverflows: has one or more slots that are higher than the instrument capacity
 * labwareWithSelfCollisions: has collisions with itself, eg slots: '1-5,2'
 * labwareWithSlotCollisions: has collisions with other labware */
export type SlotConflicts = {
  labwareWithOverflows: Array<{
    labwareId: string;
    instrumentId: string;
    capacity: number;
  }>;
  labwareWithSelfCollisions: Array<{ labwareId: string; instrumentId: string }>;
  labwareWithSlotCollisions: Array<{
    labwareId: string;
    instrumentId: string;
    slotsInUse: number[];
  }>;
};

type LabwareStartingSlotData = {
  labwareId: string;
  slots: ParsedSlot[] | null;
};

type LabwareFinalSlotData = {
  labwareId: string;
  slots: ParsedSlot[] | null;
  slotsFieldIsUndefined: boolean;
  taskId: string;
};

const _mergeLabwareWithSlotCollisions = (
  data: SlotConflicts['labwareWithSlotCollisions']
): SlotConflicts['labwareWithSlotCollisions'] => {
  const grouped = groupBy(
    data,
    (entry) => `${entry.labwareId}-${entry.instrumentId}`
  );

  return map(grouped, (group) => {
    const allSlots = flatMap(group, 'slotsInUse');
    return {
      // fallback for no unchecked in indexes
      ...(group[0] ?? { labwareId: '', instrumentId: '', slotsInUse: [] }),
      slotsInUse: uniq(allSlots),
    };
  });
};

const _deriveSlotConflicts = ({
  labwareGroupedByInstrument,
  instrumentDataById,
}: {
  labwareGroupedByInstrument: {
    [instrumentId: string]: LabwareStartingSlotData[];
  };
  instrumentDataById: { [instrumentId: string]: InstrumentForLocation };
}): SlotConflicts => {
  const labwareWithOverflows: SlotConflicts['labwareWithOverflows'] = [];
  const labwareWithSelfCollisions: SlotConflicts['labwareWithSelfCollisions'] =
    [];
  const labwareWithSlotCollisions: SlotConflicts['labwareWithSlotCollisions'] =
    [];

  for (const [instrumentId, labwareArrayForInstrument] of Object.entries(
    labwareGroupedByInstrument
  )) {
    const instrument = instrumentDataById[instrumentId];
    if (instrument === undefined) {
      logger.error(
        `cannot find start location slot conflicts, instrument ${instrumentId} does not exist`
      );
      continue;
    }

    const { capacity } = instrument;

    // Track which slots are used by which labware
    const slotToLabwareMap = new Map<number, string[]>();
    const labwareToSlotsMap = new Map<string, number[]>();
    const selfCollisionLabwareIds = new Set<string>();

    // Build maps of slot usage
    for (const { labwareId, slots } of labwareArrayForInstrument) {
      if (slots !== null) {
        const individualSlots = parseSlotsToIndividualNumbers(slots);

        // Store which slots this labware uses
        labwareToSlotsMap.set(labwareId, individualSlots);

        // Store which labware use each slot
        for (const slot of individualSlots) {
          const existingLabware = slotToLabwareMap.get(slot) || [];
          existingLabware.push(labwareId);
          slotToLabwareMap.set(slot, existingLabware);
        }

        // Check for self collisions
        const hasCollisionWithOwnSlots =
          individualSlots.length !== new Set(individualSlots).size;

        if (hasCollisionWithOwnSlots) {
          labwareWithSelfCollisions.push({
            labwareId,
            instrumentId,
          });
          selfCollisionLabwareIds.add(labwareId);
        }

        // Check for overflow
        const highestSlot = Math.max(...individualSlots);
        if (highestSlot > capacity) {
          labwareWithOverflows.push({ labwareId, instrumentId, capacity });
        }
      }
    }

    // Find labware with slot collisions
    const labwareIdsWithCollisions = new Set<string>();

    // Check each slot for multiple labware using it
    for (const [, labwareIds] of slotToLabwareMap.entries()) {
      if (labwareIds.length > 1) {
        // Add all labware using this slot to the collision set
        labwareIds.forEach((id) => labwareIdsWithCollisions.add(id));
      }
    }

    // Filter out labware with self-collisions from the collision set
    const filteredLabwareIdsWithCollisions = [
      ...labwareIdsWithCollisions,
    ].filter((id) => !selfCollisionLabwareIds.has(id));

    for (const labwareId of filteredLabwareIdsWithCollisions) {
      const otherLabwareSlots = new Set<number>();

      for (const [slot, labwareIds] of slotToLabwareMap.entries()) {
        // only add slots that are used by labware other than the current one
        if (labwareIds.some((id) => id !== labwareId)) {
          otherLabwareSlots.add(slot);
        }
      }

      labwareWithSlotCollisions.push({
        labwareId,
        instrumentId,
        slotsInUse: Array.from(otherLabwareSlots),
      });
    }
  }

  return {
    labwareWithOverflows: uniqWith(labwareWithOverflows, isEqual),
    labwareWithSlotCollisions: _mergeLabwareWithSlotCollisions(
      labwareWithSlotCollisions
    ),
    labwareWithSelfCollisions: uniqWith(labwareWithSelfCollisions, isEqual),
  };
};

/** For labware starting locations, determine which instruments have slot collisions or overflows.
 * A collision means that two or more labware share the same slot of the same instrument.
 * An overflow means that at least one slot in use is higher than the capacity of the instrument.
 * We only use labware properties that are connected to a task.
 */
export const selectStartLocationSlotConflicts = createSelector(
  selectConnectedLabwareProperties,
  selectInstrumentDataById,
  (labwareById, instrumentDataById): SlotConflicts => {
    const labwareStartLocationSlots: LabwareStartingSlotData[] = Object.values(
      labwareById
    ).map(({ id, slot }) => ({
      labwareId: id,
      slots: slot === undefined ? null : parseSlotString(slot),
    }));

    // excluding labware with no specified start location instrument, group by instrument
    const labwareGroupedByInstrument: {
      [instrumentId: string]: LabwareStartingSlotData[];
    } = groupBy(
      labwareStartLocationSlots.filter(
        ({ labwareId }) => labwareById[labwareId]?.startLocation
      ),
      ({ labwareId }) => labwareById[labwareId]?.startLocation
    );

    return _deriveSlotConflicts({
      labwareGroupedByInstrument,
      instrumentDataById,
    });
  }
);

export const selectStartLocationSlotInvalidInputs = createSelector(
  selectConnectedLabwareProperties,
  (labwareById): LabwareSerializationError[] => {
    return Object.values(labwareById)
      .filter(({ slot }) =>
        // undefined slots are valid (because we don't want to show the errors when a user has not yet entered a value)
        slot === undefined ? false : parseSlotString(slot) === null
      )
      .map(({ id }) => ({
        errorType: 'labware',
        message: LabwareErrors.LABWARE_START_LOCATION_SLOT_INVALID,
        labwareId: id,
      }));
  }
);

export const _selectFinalLocationLabwareSlotDataByInstrument = createSelector(
  selectActionTaskPropertiesStrict,
  selectInstrumentDataById,
  selectFinalTasksByLabwareId,
  (
    actionTaskPropertiesById,
    instrumentDataById,
    finalTasksByLabwareId
  ): { [instrumentId: string]: LabwareFinalSlotData[] } => {
    return Object.entries(finalTasksByLabwareId).reduce<{
      [instrumentId: string]: LabwareFinalSlotData[];
    }>((acc, [labwareId, taskId]) => {
      const task = actionTaskPropertiesById[taskId];
      const instrumentId = task?.instrumentId;
      const instrument =
        typeof instrumentId === 'string'
          ? instrumentDataById[instrumentId]
          : undefined;
      if (
        task === undefined ||
        typeof instrumentId !== 'string' ||
        instrument === undefined
      ) {
        logger.warn(
          `selectFinalLocationSlotConflicts could not get task ${taskId} and/or its instrument ${instrumentId}`
        );
        // incomplete or missing data, give up
        return acc;
      }

      if (labwareId === null) {
        return acc;
      }

      let parsedSlots: ParsedSlot[] | null;
      const rawSlots = task.taskParameters?.slot;
      const { capacity } = instrument;

      if (capacity === 1) {
        // assume instruments with a capacity of 1 have no slot field
        // and inherently have any and all labware assigned to slot 1
        parsedSlots = [1];

        if (rawSlots !== undefined) {
          logger.warn(
            `Instrument ${instrumentId} has capacity of 1, so we expect its 'slot' parameter to be undefined. ` +
              `However, its slot parameter is ${JSON.stringify(rawSlots)}`
          );
        }
      } else {
        parsedSlots =
          typeof rawSlots === 'string' ? parseSlotString(rawSlots) : null;
      }

      return {
        ...acc,
        [instrumentId]: [
          ...(acc[instrumentId] ?? []),
          {
            labwareId,
            slotsFieldIsUndefined: rawSlots === undefined,
            slots: parsedSlots,
            taskId,
          },
        ],
      };
    }, {});
  }
);

export const selectFinalLocationSlotConflicts = createSelector(
  selectInstrumentDataById,
  _selectFinalLocationLabwareSlotDataByInstrument,
  (instrumentDataById, labwareGroupedByInstrument): SlotConflicts =>
    _deriveSlotConflicts({
      labwareGroupedByInstrument,
      instrumentDataById,
    })
);

type SlotMappingAny = Record<string, string | number | null | undefined>;
type SlotToLabwareMap = Record<number, string[]>;

const _toNumberOrNull = (
  v: string | number | null | undefined
): number | null => {
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : null;
  }
  if (typeof v === 'string') {
    const parsed = parseInt(v, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const _findSlotCollision = (
  slotMapping?: SlotMappingAny
): string | null | undefined => {
  if (!slotMapping) {
    return null;
  }

  const slotToLabwareIds = Object.entries(slotMapping).reduce<SlotToLabwareMap>(
    (acc, [labwareId, raw]) => {
      const slot = _toNumberOrNull(raw);
      if (slot === null) {
        return acc;
      }
      acc[slot] = [...(acc[slot] || []), labwareId];
      return acc;
    },
    {}
  );

  const collision =
    Object.values(slotToLabwareIds).find(
      (labwareIds) => labwareIds.length > 1
    ) || null;

  return collision ? collision[0] : null;
};

const _createCollisionError = (params: {
  labwareId: string;
  instrumentId: string | null;
  taskId: string;
}): LabwareSerializationError => {
  return {
    errorType: 'labware',
    message: LabwareErrors.LABWARE_SLOT_COLLISION,
    ...params,
  };
};

export const selectSlotCollisionConflicts = createSelector(
  selectActionTaskPropertiesStrict,
  (actionTaskPropertiesById): LabwareSerializationError[] => {
    return Object.values(actionTaskPropertiesById)
      .map((task) => {
        const inputCollision = _findSlotCollision(
          task.labwareIdsToInputSlots as SlotMappingAny | undefined
        );
        const outputCollision = _findSlotCollision(
          task.labwareIdsToOutputSlots as SlotMappingAny | undefined
        );
        const collidingLabwareId = inputCollision || outputCollision;

        if (!collidingLabwareId) {
          return null;
        }

        return _createCollisionError({
          labwareId: collidingLabwareId,
          instrumentId: task.instrumentId,
          taskId: task.id,
        });
      })
      .filter((error): error is LabwareSerializationError => error !== null);
  }
);
